"use client";

import { FC, useState, TouchEvent, MouseEvent, useEffect } from "react";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import { X } from "lucide-react";
import { TbLassoPolygon } from "react-icons/tb";
import { MoreVertical, Plus, Zap, Printer } from "lucide-react";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { Image } from "@/types/thread";
import { AlertDialogType } from "@/types/alert";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { addAlertDialog, getNewGenerations } from "@/redux/actions";
import { useGenContext } from "@/context/GenContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DownloadButton from "@/components/common/DownloadButton";
import RatingControls from "@/components/social/ratings/RatingControls";
import EnhanceImage from "@/components/idea/EnhanceImage";
import InpaintModal from "@/components/idea/GenInputs/InpaintModal";

const MIN_SWIPE_DISTANCE = 50;

interface MobileImageViewerProps {
  images: Image[];
  onClose: () => void;
  initialSelection?: number;
  roomUUID?: string;
  showControlsOnlyAfterLoad?: boolean;
}

const MobileImageViewer: FC<MobileImageViewerProps> = ({
  images,
  onClose,
  initialSelection = 0,
  roomUUID,
  showControlsOnlyAfterLoad = true,
}) => {
  const { classes, cx } = useStyles();
  const dispatch = useAppDispatch();
  const { dispatch: genDispatch, addImage, state } = useGenContext();
  const { isEditingMask } = state;
  const [imageLoaded, setImageLoaded] = useState(false);

  const [selectedImage, setSelectedImage] = useState(initialSelection);
  const [touchStart, setTouchStart] = useState<Nullable<number>>(null);
  const [touchEnd, setTouchEnd] = useState<Nullable<number>>(null);
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const [isUpscaling, setIsUpscaling] = useState(false);

  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");
  const hasActiveJobs = Boolean(user?.activeJobs && user.activeJobs.length > 0);
  const quotas = useAppSelector((state) => state.user.quotas);
  const inpaint = quotas.find((quota) => quota.resourceName === "inPaint");
  const dailyInpaintUsed = inpaint ? (inpaint.currentQuota ?? 0) <= 0 : false;

  useEffect(() => {
    setImageLoaded(false);
  }, [selectedImage]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleNext = () => {
    if (selectedImage === images.length - 1) {
      setSelectedImage(0);
    } else {
      setSelectedImage(selectedImage + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedImage === 0) {
      setSelectedImage(images.length - 1);
    } else {
      setSelectedImage(selectedImage - 1);
    }
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  const handleEditImage = (e: MouseEvent<HTMLElement>, useInpaint: boolean) => {
    e.stopPropagation();

    const currentImage = images[selectedImage];

    if (!user) {
      dispatch(
        addAlertDialog({
          title: "Account Required",
          description:
            "Please sign in or create a free account to edit this image",
          prevButtonText: "Sign In",
          nextButtonText: "Create Account",
          type: AlertDialogType.AccountRequired,
        })
      );
      return;
    }

    if (useInpaint) {
      if (dailyInpaintUsed) {
        dispatch(
          addAlertDialog({
            title: "Subscription Required",
            description: "Upgrade to continue using Replace!",
            nextButtonText: "Upgrade",
            type: AlertDialogType.SubscriptionRequired,
          })
        );
        return;
      }

      genDispatch({ type: "SET_SELECTED_IMAGE", payload: currentImage.url });
      genDispatch({ type: "SET_IS_EDITING_MASK", payload: true });
      genDispatch({ type: "SET_STROKES", payload: [] });
      genDispatch({ type: "SET_IMAGES", payload: [] });
    } else {
      addImage(currentImage.url);
      onClose();
    }
  };

  const handleShowEnhanceModal = () => {
    setShowEnhanceModal(true);
    onClose();
  };

  const handleUpscaleImage = async () => {
    if (isUpscaling || hasActiveJobs) return;

    if (!isSubscribed) {
      dispatch(
        addAlertDialog({
          title: "Subscription Required",
          description: "Upgrade to upscale this image!",
          nextButtonText: "Upgrade",
          type: AlertDialogType.SubscriptionRequired,
        })
      );
      onClose();
      return;
    }

    setIsUpscaling(true);

    try {
      await dispatch(
        getNewGenerations({ upscale: true, imageId: images[selectedImage].id })
      ).unwrap();
      onClose();
      setTimeout(() => {
        setIsUpscaling(false);
      }, 1000);
    } catch (error) {
      console.error("Error in handleUpscaleImage:", error);
      setIsUpscaling(false);
    }
  };

  if (images.length === 0) return null;

  const currentImage = images[selectedImage];

  return (
    <>
      <Modal open={!isEditingMask} className={classes.modal}>
        <div className={classes.container}>
          <div className={classes.header}>
            <IconButton onClick={onClose} className={classes.closeButton}>
              <X size={24} />
            </IconButton>
          </div>

          <div
            className={classes.imageContainer}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className={classes.imageWrapper}>
              <img
                src={currentImage.url}
                alt={currentImage.alt || ""}
                className={classes.image}
                onLoad={handleImageLoad}
              />

              {(!showControlsOnlyAfterLoad || imageLoaded) && (
                <div className={classes.controls}>
                  <DownloadButton url={currentImage.url} color="white" />
                  <div>
                    <RatingControls
                      imageId={currentImage.id}
                      likesCount={currentImage.likesCount || 0}
                      userRating={currentImage.userRating || null}
                      hideLikesCount
                      className={classes.smallerRatingControls}
                    />
                  </div>
                  {roomUUID && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <IconButton aria-label="Edit Controls">
                          <MoreVertical className="tw:h-6 tw:w-6 tw:text-white tw:filter tw:drop-shadow-[0px_0px_1px_#1E222A] tw:hover:scale-110" />
                        </IconButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="tw:z-[2100]">
                        <DropdownMenuItem
                          onClick={(e) => handleEditImage(e, false)}
                        >
                          <span className="tw:flex-1">Add to Prompt</span>
                          <Plus className="tw:ml-2 tw:h-4 tw:w-4" />
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => handleEditImage(e, true)}
                        >
                          <span className="tw:flex-1">Replace Area</span>
                          <TbLassoPolygon className="tw:ml-2 tw:h-4 tw:w-4" />
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleShowEnhanceModal}>
                          <span className="tw:flex-1">Enhance</span>
                          <Zap className="tw:ml-2 tw:h-4 tw:w-4" />
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={handleUpscaleImage}
                          disabled={isUpscaling || hasActiveJobs}
                        >
                          <span className="tw:flex-1">
                            {isUpscaling ? "Upscaling..." : "Upscale"}
                          </span>
                          <Printer className="tw:ml-2 tw:h-4 tw:w-4" />
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              )}
            </div>

            {images.length > 1 && (
              <div className={classes.dotsContainer}>
                {images.map((_, index) => (
                  <div
                    key={`dot-${index}`}
                    className={cx(
                      classes.dot,
                      index === selectedImage && classes.dotActive
                    )}
                    onClick={() => setSelectedImage(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <InpaintModal />

      <EnhanceImage
        image={currentImage}
        modalOpen={showEnhanceModal}
        setModalOpen={setShowEnhanceModal}
        hideIcon
      />
    </>
  );
};

export default MobileImageViewer;
